import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Mail, Lock, Star, Tag, Crown, Trash2, Loader2, AlertTriangle, LogIn, ShoppingBag, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, token, logout } = useAuth();
  const { t } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);

  // Form states
  const [emailForm, setEmailForm] = useState({ new_email: '', password: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Rotating quotes
  const profileQuotes = [
    t('profile.quote1'),
    t('profile.quote2'),
    t('profile.quote3')
  ];
  const [quoteIndex, setQuoteIndex] = useState(0);

  const fetchProfile = useCallback(async () => {
    try {
      const [profileRes, ordersRes] = await Promise.all([
        axios.get(`${API}/user/profile`),
        axios.get(`${API}/orders`)
      ]);
      setProfile(profileRes.data);
      setOrders(ordersRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchProfile();
    else setLoading(false);
  }, [token, fetchProfile]);

  useEffect(() => {
    const interval = setInterval(() => {
      setQuoteIndex(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      const res = await axios.put(`${API}/user/email`, {
        new_email: emailForm.new_email,
        password: emailForm.password
      });
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      }
      toast.success(t('profile.successEmail'));
      setEmailForm({ new_email: '', password: '' });
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('profile.errorGeneric'));
    } finally {
      setSavingEmail(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm) {
      toast.error(t('profile.passwordMismatch'));
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error(t('profile.passwordTooShort'));
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/user/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success(t('profile.successPassword'));
      setPasswordForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('profile.errorGeneric'));
    } finally {
      setSavingPassword(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm(t('profile.cancelConfirm'))) return;
    try {
      await axios.put(`${API}/user/subscription/cancel`);
      toast.success(t('profile.successCancel'));
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('profile.errorGeneric'));
    }
  };

  const handleDeleteProfile = async () => {
    if (!window.confirm(t('profile.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/user/profile`);
      toast.success(t('profile.successDelete'));
      logout();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('profile.errorGeneric'));
    }
  };

  // Not logged in
  if (!token) {
    return (
      <div className="min-h-screen bg-[#F4EDE6] flex items-center justify-center" data-testid="profile-not-logged-in">
        <div className="text-center p-8">
          <LogIn className="w-16 h-16 text-[#C4A77D] mx-auto mb-6" />
          <h1 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mb-4">{t('profile.notLoggedIn')}</h1>
          <p className="text-[#595959] mb-6">{t('profile.notLoggedInDesc')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4EDE6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C4A77D] animate-spin" />
      </div>
    );
  }

  const statusKey = profile?.membership?.status === 'active' ? 'statusActive' 
    : profile?.membership?.status === 'cancelled' ? 'statusCancelled' 
    : 'statusInactive';

  return (
    <div className="min-h-screen bg-[#F4EDE6]" data-testid="profile-page">
      {/* Header */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        <video 
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
          src="/profile-video.mp4"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="container-main text-center text-white relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <User className="w-16 h-16 mx-auto mb-4 opacity-80" />
            <h1 className="font-['Avenir_Next'] text-4xl md:text-5xl mb-3">{t('profile.welcome')} {profile?.name}</h1>
            <div className="h-8 mb-3">
              <AnimatePresence mode="wait">
                <motion.p
                  key={quoteIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  className="text-white/90 text-lg italic"
                >
                  {profileQuotes[quoteIndex]}
                </motion.p>
              </AnimatePresence>
            </div>
            <p className="text-white/70">{profile?.email}</p>
            {profile?.created_at && (
              <p className="text-white/60 text-sm mt-1">
                {t('profile.memberSince')} {new Date(profile.created_at).toLocaleDateString('sv-SE')}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      <div className="container-main py-12">
        <div className="max-w-3xl mx-auto space-y-8">

          {/* Points & Membership Row */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Points */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-8 text-center"
              data-testid="profile-points"
            >
              <Star className="w-10 h-10 text-[#C4A77D] mx-auto mb-4" />
              <h2 className="font-['Avenir_Next'] text-xl text-[#1A1A1A] mb-2">{t('profile.pointsTitle')}</h2>
              <p className="font-['Avenir_Next'] text-5xl text-[#C4A77D] mb-1">{profile?.points || 0}</p>
              <p className="text-[#8C8C8C] text-sm">{t('profile.points')}</p>
            </motion.div>

            {/* Membership */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 text-center"
              data-testid="profile-membership"
            >
              <Crown className="w-10 h-10 text-[#C4A77D] mx-auto mb-4" />
              <h2 className="font-['Avenir_Next'] text-xl text-[#1A1A1A] mb-4">{t('profile.subscriptionTitle')}</h2>
              {profile?.membership?.plan ? (
                <div className="space-y-2">
                  <p className="text-[#595959]">
                    <span className="font-medium">{t('profile.plan')}:</span>{' '}
                    <span className="capitalize">{profile.membership.plan}</span>
                  </p>
                  <p className="text-[#595959]">
                    <span className="font-medium">{t('profile.status')}:</span>{' '}
                    <span className={profile.membership.status === 'active' ? 'text-green-600' : 'text-red-500'}>
                      {t(`profile.${statusKey}`)}
                    </span>
                  </p>
                  {profile.membership.status === 'active' && (
                    <Button
                      onClick={handleCancelSubscription}
                      variant="outline"
                      className="mt-4 border-red-300 text-red-500 hover:bg-red-50"
                      data-testid="cancel-subscription-btn"
                    >
                      {t('profile.cancelSubscription')}
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-[#8C8C8C]">{t('profile.statusInactive')}</p>
              )}
            </motion.div>
          </div>

          {/* Discount Codes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-8"
            data-testid="profile-discounts"
          >
            <div className="flex items-center gap-3 mb-6">
              <Tag className="w-6 h-6 text-[#C4A77D]" />
              <h2 className="font-['Avenir_Next'] text-xl text-[#1A1A1A]">{t('profile.discountTitle')}</h2>
            </div>
            {profile?.discount_codes?.length > 0 ? (
              <div className="space-y-3">
                {profile.discount_codes.map((dc, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-[#F4EDE6] rounded-xl">
                    <div>
                      <p className="font-mono font-medium text-[#1A1A1A]">{dc.code}</p>
                      <p className="text-sm text-[#8C8C8C]">{dc.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-[#C4A77D]">{dc.discount}</p>
                      {dc.valid_until && (
                        <p className="text-xs text-[#8C8C8C]">{t('profile.valid')} {dc.valid_until}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#8C8C8C] text-center py-4">{t('profile.noDiscounts')}</p>
            )}
          </motion.div>

          {/* Orders */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-2xl p-8"
            data-testid="profile-orders"
          >
            <div className="flex items-center gap-3 mb-6">
              <ShoppingBag className="w-6 h-6 text-[#C4A77D]" />
              <h2 className="font-['Avenir_Next'] text-xl text-[#1A1A1A]">{t('profile.ordersTitle')}</h2>
            </div>

            {orders.length > 0 ? (
              <>
                {/* Statistics */}
                {(() => {
                  const allItems = orders.flatMap(o => o.items || []);
                  const totalSpent = allItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
                  const categoryCount = {};
                  allItems.forEach(item => {
                    const cat = item.category || t('profile.statsOther');
                    categoryCount[cat] = (categoryCount[cat] || 0) + (item.quantity || 1);
                  });
                  const sortedCategories = Object.entries(categoryCount).sort((a, b) => b[1] - a[1]);
                  const maxCount = sortedCategories.length > 0 ? sortedCategories[0][1] : 1;

                  return (
                    <div className="mb-8" data-testid="orders-statistics">
                      {/* Stats summary */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-[#F4EDE6] rounded-xl p-4 text-center">
                          <p className="font-['Avenir_Next'] text-2xl text-[#C4A77D]">{orders.length}</p>
                          <p className="text-xs text-[#8C8C8C]">{t('profile.statsTotalOrders')}</p>
                        </div>
                        <div className="bg-[#F4EDE6] rounded-xl p-4 text-center">
                          <p className="font-['Avenir_Next'] text-2xl text-[#C4A77D]">{allItems.length}</p>
                          <p className="text-xs text-[#8C8C8C]">{t('profile.statsTotalExperiences')}</p>
                        </div>
                        <div className="bg-[#F4EDE6] rounded-xl p-4 text-center">
                          <p className="font-['Avenir_Next'] text-2xl text-[#C4A77D]">{totalSpent.toLocaleString('sv-SE')}</p>
                          <p className="text-xs text-[#8C8C8C]">{t('profile.statsTotalSpent')}</p>
                        </div>
                      </div>

                      {/* Category breakdown */}
                      {sortedCategories.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <BarChart3 className="w-4 h-4 text-[#C4A77D]" />
                            <p className="text-sm font-medium text-[#1A1A1A]">{t('profile.statsByCategory')}</p>
                          </div>
                          <div className="space-y-2">
                            {sortedCategories.map(([cat, count]) => (
                              <div key={cat} className="flex items-center gap-3">
                                <span className="text-sm text-[#595959] w-36 truncate">{cat}</span>
                                <div className="flex-1 h-6 bg-[#F4EDE6] rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-[#C4A77D] rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                                    style={{ width: `${Math.max((count / maxCount) * 100, 15)}%` }}
                                  >
                                    <span className="text-xs text-white font-medium">{count}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Order list */}
                <div className="space-y-4">
                  {orders.map((order, i) => (
                    <div key={order.id || i} className="p-4 bg-[#F4EDE6] rounded-xl">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-sm text-[#8C8C8C]">
                            {t('profile.orderDate')}: {order.created_at ? new Date(order.created_at).toLocaleDateString('sv-SE') : '-'}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${order.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {order.status === 'confirmed' ? t('profile.orderConfirmed') : t('profile.orderPending')}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {(order.items || []).map((item, j) => (
                          <div key={item.id || j} className="flex justify-between items-center">
                            <span className="text-[#1A1A1A]">{item.name}</span>
                            <span className="text-[#C4A77D] font-medium">{item.quantity}x {item.price} kr</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-[#8C8C8C] text-center py-4">{t('profile.noOrders')}</p>
            )}
          </motion.div>

          {/* Change Email */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <Mail className="w-6 h-6 text-[#C4A77D]" />
              <h2 className="font-['Avenir_Next'] text-xl text-[#1A1A1A]">{t('profile.changeEmail')}</h2>
            </div>
            <form onSubmit={handleChangeEmail} className="space-y-4" data-testid="change-email-form">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">{t('profile.newEmail')}</Label>
                <Input
                  type="email"
                  value={emailForm.new_email}
                  onChange={e => setEmailForm({ ...emailForm, new_email: e.target.value })}
                  required
                  className="h-12 rounded-xl"
                  data-testid="new-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">{t('profile.passwordToConfirm')}</Label>
                <Input
                  type="password"
                  value={emailForm.password}
                  onChange={e => setEmailForm({ ...emailForm, password: e.target.value })}
                  required
                  className="h-12 rounded-xl"
                  data-testid="email-password-input"
                />
              </div>
              <Button type="submit" disabled={savingEmail} className="bg-[#C4A77D] hover:bg-[#B39669] text-white rounded-full h-12 px-8" data-testid="update-email-btn">
                {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : t('profile.updateEmail')}
              </Button>
            </form>
          </motion.div>

          {/* Change Password */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-6 h-6 text-[#C4A77D]" />
              <h2 className="font-['Avenir_Next'] text-xl text-[#1A1A1A]">{t('profile.changePassword')}</h2>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">{t('profile.currentPassword')}</Label>
                <Input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={e => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  required
                  className="h-12 rounded-xl"
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">{t('profile.newPassword')}</Label>
                <Input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={e => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  required
                  className="h-12 rounded-xl"
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">{t('profile.confirmPassword')}</Label>
                <Input
                  type="password"
                  value={passwordForm.confirm}
                  onChange={e => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                  required
                  className="h-12 rounded-xl"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button type="submit" disabled={savingPassword} className="bg-[#C4A77D] hover:bg-[#B39669] text-white rounded-full h-12 px-8" data-testid="update-password-btn">
                {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : t('profile.updatePassword')}
              </Button>
            </form>
          </motion.div>

          {/* Danger Zone - Delete Profile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl p-8 border border-red-200"
            data-testid="profile-danger-zone"
          >
            <div className="flex items-center gap-3 mb-4">
              <Trash2 className="w-6 h-6 text-red-500" />
              <h2 className="font-['Avenir_Next'] text-xl text-red-600">{t('profile.deleteTitle')}</h2>
            </div>
            <p className="text-[#595959] mb-6">{t('profile.deleteDesc')}</p>
            <Button
              onClick={handleDeleteProfile}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="delete-profile-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              {t('profile.deleteBtn')}
            </Button>
          </motion.div>

        </div>
      </div>
    </div>
  );
}
